// =============================================================================
//  Copyright   : 2019 semantics GmbH
//  Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 156393 $
//  Purpose     : call auth apis
// =============================================================================
'use strict'
// ---Third-party imports
import {
  put,
  take,
  takeEvery,
  select,
  call,
  putResolve,
  throttle,
  actionChannel,
} from 'redux-saga/effects'
import base64js from 'base64-js'

// ---Framework imports
import {buffers} from 'uix/sagas'
import {Actions, Sel} from 'uix/registries'
import Api from 'uix/api'

import log from 'uix/logging'
import {beginWebauthn, setWebauthn, loginWebauthn, queryWebauthn, setUserData} from './actions'
import {dict, get, mapItems, uniq} from 'ldash'
import {timeouts} from 'uix/utils'

// ---Globals

function b64enc(buf) {
  return base64js
    .fromByteArray(buf)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

function b64RawEnc(buf) {
  return base64js
    .fromByteArray(buf)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
}

function hexEncode(buf) {
  return Array.from(buf)
    .map(function(x) {
      return ('0' + x.toString(16)).substr(-2)
    })
    .join('')
}

const transformCredentialCreateOptions = credentialCreateOptionsFromServer => {
  /* for registration */
  let {challenge, user} = credentialCreateOptionsFromServer
  user = {
    ...user,
    id: Uint8Array.from(atob(credentialCreateOptionsFromServer.user.id), c => c.charCodeAt(0)),
  }

  challenge = Uint8Array.from(atob(credentialCreateOptionsFromServer.challenge), c =>
    c.charCodeAt(0)
  )

  const transformedCredentialCreateOptions = {...credentialCreateOptionsFromServer, challenge, user}
  return transformedCredentialCreateOptions
}

const transformCredentialRequestOptions = credentialRequestOptionsFromServer => {
  /* for login */
  let {challenge, allowCredentials} = credentialRequestOptionsFromServer

  challenge = Uint8Array.from(atob(challenge), c => c.charCodeAt(0))

  allowCredentials = allowCredentials.map(credentialDescriptor => {
    let {id} = credentialDescriptor
    id = id.replace(/\_/g, '/').replace(/\-/g, '+')
    id = Uint8Array.from(atob(id), c => c.charCodeAt(0))
    return {...credentialDescriptor, id}
  })
  const transformedCredentialRequestOptions = {
    ...credentialRequestOptionsFromServer,
    challenge,
    allowCredentials,
  }

  return transformedCredentialRequestOptions
}

const transformNewAssertionForServer = newAssertion => {
  /* for registration */
  const attObj = new Uint8Array(newAssertion.response.attestationObject)
  const clientDataJSON = new Uint8Array(newAssertion.response.clientDataJSON)
  const rawId = new Uint8Array(newAssertion.rawId)

  const registrationClientExtensions = newAssertion.getClientExtensionResults()

  return {
    id: newAssertion.id,
    rawId: b64enc(rawId),
    type: newAssertion.type,
    attObj: b64enc(attObj),
    clientData: b64enc(clientDataJSON),
    registrationClientExtensions: JSON.stringify(registrationClientExtensions),
  }
}

const transformAssertionForServer = newAssertion => {
  /* for login */
  const authData = new Uint8Array(newAssertion.response.authenticatorData)
  const clientDataJSON = new Uint8Array(newAssertion.response.clientDataJSON)
  const rawId = new Uint8Array(newAssertion.rawId)
  const sig = new Uint8Array(newAssertion.response.signature)
  const assertionClientExtensions = newAssertion.getClientExtensionResults()

  return {
    id: newAssertion.id,
    rawId: b64enc(rawId),
    type: newAssertion.type,
    authData: b64RawEnc(authData),
    clientData: b64RawEnc(clientDataJSON),
    signature: hexEncode(sig),
    assertionClientExtensions: JSON.stringify(assertionClientExtensions),
  }
}

function* _verifyRegistrationCredential(username, credential) {
  let verify_response = JSON.stringify(transformNewAssertionForServer(credential))
  let {data, error} = yield Api.post('/api/webauthn/register', {username, verify_response})
  if (error) {
    yield put(setWebauthn({state: 'ERROR_REG_CREDENTIALS_VERIFY', error, available: false}))
  } else {
    yield put(setWebauthn({state: 'REG_COMPLETE', available: true}))
  }
}

function* _beginWebauthn({payload}) {
  let {name: username, fullname} = payload.user
  if (!username) {
    log.error('no username')
    return
  }

  yield putResolve(setWebauthn({state: 'REG_BEGIN', error: null}))
  let {data, error} = yield Api.get('/api/webauthn/begin', {
    username,
    display_name: fullname || 'Anon',
    reset: String(Boolean(payload.options.reset)),
  })

  if (error) {
    yield put(setWebauthn({state: 'ERROR_REG_BEGIN', error}))
  }
  if (data) {
    // request the authenticator(s) to create a new credential keypair.
    let credential = null
    try {
      yield putResolve(setWebauthn({state: 'REG_OPTIONS_CREATED', user: data.user}))
      let opts = {publicKey: transformCredentialCreateOptions(data)}
      // speak with hardware (yubikey, windows hello)
      credential = yield call(() => navigator.credentials.create(opts))
      // speak with server
      yield _verifyRegistrationCredential(username, credential)
    } catch (err) {
      // XXX: details of credentials.create JS error?
      yield put(setWebauthn({state: 'ERROR_REG_CREDENTIALS_CREATE', error: null, available: false}))
      log.error('Error creating registration credential: %o', err)
    }
  }
}

function* _verifyLoginCredential(username, assertion) {
  let verify_response = JSON.stringify(transformAssertionForServer(assertion))
  let {data, error} = yield Api.post('/api/webauthn/verify', {username, verify_response})
  if (error) {
    yield put(setWebauthn({state: 'ERROR_LOGIN_CREDENTIALS_VERIFY', error, available: false}))
    return {error, success: false}
  } else {
    yield put(setWebauthn({state: 'LOGIN_COMPLETE', available: true}))
    return {user: data, success: true}
  }
}

function* _loginWebauthn({payload}) {
  /* run webauthn registration */
  let {username} = payload

  yield putResolve(setWebauthn({state: 'LOGIN_BEGIN', error: null}))
  let {data, error} = yield Api.get('/api/webauthn/login', {username})

  if (error) {
    yield put(setWebauthn({state: 'ERROR_LOGIN_BEGIN', error, available: false}))
  } else if (data) {
    // request the authenticator to create an assertion signature using the
    // credential private key
    let assertion = null
    try {
      yield putResolve(setWebauthn({state: 'LOGIN_OPTIONS_CREATED'}))
      let opts = {publicKey: transformCredentialRequestOptions(data)}
      // speak with yubikey
      assertion = yield call(() => navigator.credentials.get(opts))
      // speak with server
      let {success, user} = yield _verifyLoginCredential(username, assertion)
      return {success, user}
    } catch (err) {
      console.error(err)
      yield put(
        setWebauthn({state: 'ERROR_LOGIN_CREDENTIALS_CREATE', error: null, available: false})
      )
      log.error('Error creating login credential: %o', err)
    }
  }
  return {success: false}
}

function* _queryWebauthn({payload}) {
  /* check if user is webauthn registered */
  let {username} = payload

  yield putResolve(setWebauthn({available: 'LOADING'}))
  let {data, error} = yield Api.get('/api/webauthn/query', {username})
  if (error) {
    yield put(setWebauthn({state: 'ERROR_QUERY', error}))
  } else if (data) {
    yield put(setWebauthn({available: data.available === true}))
  }
}

function* _login(username, password) {
  //log(username, password)
  let {data, error} = yield Api.post(
    '/api/login',
    {loginUser: username, loginPassword: password},
    {timeout: timeouts.LOGIN}
  )
  if (data) {
    return {success: true, user: data}
  } else {
    return {success: false, error}
  }
}

function* onLoginSuccess(user) {
  let prev_user = yield select(Sel.user)
  let prev_app_state = yield select(Sel.appstate)
  // session expiration leads to VIEW_UNAUTHORIZED
  // special case reinit of app, if user stays the same we simply switch back to APP_RUNNING
  let need_reinit = user => prev_app_state !== 'VIEW_UNAUTHORIZED' || prev_user.name !== user.name

  yield putResolve(Actions.setUser({user}))
  if (need_reinit(user)) {
    yield putResolve(Actions.setAppState({state: 'INIT_APP'}))
  } else {
    yield putResolve(Actions.setAppState({state: 'APP_RUNNING'}))
  }
}

function* login({payload}) {
  let {username, password} = payload

  if (!password) {
    let {success, user} = yield call(_loginWebauthn, {payload: {username}})
    if (success) {
      yield onLoginSuccess(user)
    } else {
      let webauthnState = yield select(root => root.meta.webauthn.state)
      yield put(Actions.loginFailed(username, `WEBAUTHN_${webauthnState}`))
    }
    return
  } else {
    let {success, user, error} = yield call(_login, username, password)
    if (success) {
      yield onLoginSuccess(user)
      // XXX: do we really wait for finished init here?
      // loginSuccess anticipates a finished init
      yield put(Actions.loginSuccess())
    } else {
      yield put(Actions.loginFailed(username, error.reason))
    }
  }
}

function* logout({payload}) {
  // this will trigger a new session with guest state
  let {success, user} = yield call(Api.logout)
  if (!success) {
    log.error('logout failure')
  } else if (!user.groups.includes('guest')) {
    log.warn('reached no guest user state: %o', user.groups)
  }
  yield put(Actions.setAppState({state: 'INIT_APP'}))
}

function* impersonify({payload}) {
  /* does only work with 'debug' rights
   */
  let {data: user, error} = yield Api.post('/api/login', {impersonify: payload.user.login})
  if (user.name == payload.user.login) {
    yield put(Actions.setUser({user}))
    yield put(Actions.successNotification({message: `Switched user context: ${user.name}`}))
    yield put(Actions.reloadAllParts())
  } else {
    yield put(Actions.errorNotification({message: `Failure switching user context: ${user.name}`}))
  }
  //~ yield put(Actions.setAppState({state: 'INIT_APP'}))
}

function* watchGetUsers() {
  let chan = yield actionChannel('GET_USER_DATA', buffers.expanding(100))
  while (true) {
    let {payload} = yield take(chan)
    let users = yield select(root => root.data.users)
    let puids = payload.puids || [payload.puid]

    puids = puids.filter(puid => !users[puid])
    // users with null are also marked for loading
    let more_puids = mapItems(users, (puid, user) => puid, (puid, user) => user === null)
    puids = uniq([...puids, ...more_puids].map(String))
    if (puids.length == 0) {
      continue
    }

    log.debug(`fetching ${puids.length} users`)
    let {data, error} = yield Api.post('/api/getUserInfo', {puids: puids.join(',')})

    let new_userdata = {}
    if (data?.users) {
      new_userdata = dict(data.users.map(user => [user.puid, user]))
    } else {
      log.error('error fetching user data')
    }
    // bad_userdata has only a puid
    let bad_userdata = dict(puids.filter(puid => !new_userdata[puid]).map(puid => [puid, {puid}]))
    yield put(setUserData({...new_userdata, ...bad_userdata}))
  }
}

export const sagas = {
  queryWebauthn: throttle(500, queryWebauthn, _queryWebauthn),
  beginWebauthn: takeEvery(beginWebauthn, _beginWebauthn),
  loginWebauthn: takeEvery(loginWebauthn, _loginWebauthn),
  login: takeEvery('RUN_LOGIN', login),
  logout: takeEvery('RUN_LOGOUT', logout),
  impersonify: takeEvery('RUN_IMPERSONIFY', impersonify),
  watchGetUsers: watchGetUsers(),
}
